import * as React from 'react';

/**
 * Shell-level actions for content rendered inside StudioApp that the shell
 * does not own — the host `home` slot, the tool dock, the share dialog. The
 * workspace context holds document state; this holds what only the shell can
 * do (the file picker lives on its hidden input, the dialog on its state).
 */
export interface StudioShellValue {
  layout: 'workbench' | 'canvas';
  /** Open the PDF file picker. A tool still waiting for a file (see `selectTool`) stops waiting. */
  openFileDialog: () => void;
  /**
   * Rail-click semantics: a document tool with no document opens the picker
   * first and opens with the file it returns. Cancelling the picker, or a
   * document that arrives any other way, ends the wait.
   */
  selectTool: (id: string) => void;
  /**
   * True while a file from the desktop is dragged over the Studio root. Text,
   * links and images dragged within the page do not raise it.
   */
  dragging: boolean;
  /** Open the share dialog on a section (canvas layout; no-op in workbench). */
  openShare: (section?: 'link' | 'export') => void;
  /**
   * Scroll the main viewport to a page of the open document and make it the
   * current page, which it stays once the scroll settles; below `sm` the open
   * tool's sheet closes so the page shows, and the page takes the focus.
   * False, and nothing moves, for a page the document does not have.
   */
  goToPage: (page: number) => boolean;
}

const StudioShellContext = React.createContext<StudioShellValue | null>(null);

export const StudioShellProvider = StudioShellContext.Provider;

export function useStudioShell(): StudioShellValue {
  const context = React.useContext(StudioShellContext);
  if (!context) throw new Error('useStudioShell must be used inside <StudioApp>');
  return context;
}
